import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { getCategories, getCollections, getHeroApi } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import ScrollReveal from "@/components/ScrollReveal";
import TrustStrip from "@/components/TrustStrip";
import Testimonials from "@/components/Testimonials";
import Seo from "@/components/Seo";
import Img from "@/components/Img";

const IMG = {
  hero: "/api/media/lemce/hero/lemce-hero-296aeda365.png",
  macroBrown: "https://images.unsplash.com/photo-1594332495179-d979bcd18142?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHw0fHxtYWNybyUyMHBob3RvZ3JhcGh5JTIwcHJlbWl1bSUyMGNvdHRvbiUyMGZhYnJpYyUyMHRleHR1cmV8ZW58MHx8fHwxNzg3MDQ3NTc0fDA&ixlib=rb-4.1.0&q=85",
  macroYarn: "https://images.unsplash.com/photo-1670764732262-331943e5af5e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwzfHxtYWNybyUyMGZhYnJpYyUyMHRleHR1cmUlMjBrbml0JTIwd29vbCUyMGNsb3NlJTIwdXB8ZW58MHx8fHwxNzg3MDQ3NjcwfDA&ixlib=rb-4.1.0&q=85",
  macroCable: "https://images.pexels.com/photos/6757412/pexels-photo-6757412.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720",
  lifestyle: "https://images.unsplash.com/photo-1608975321561-176c1b187d24?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxlZGl0b3JpYWwlMjBmYXNoaW9uJTIwbWFuJTIwY296eSUyMGtuaXR3ZWFyJTIwc3R1ZGlvfGVufDB8fHx8MTc4NzA0NzY3MHww&ixlib=rb-4.1.0&q=85",
  closing: "https://images.pexels.com/photos/20026247/pexels-photo-20026247.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1600",
};

const USP = [
  { title: "Premium Materials", body: "Soft, carefully selected materials designed for superior everyday comfort.", img: IMG.macroYarn },
  { title: "Lightweight Feel", body: "Engineered to feel exceptionally light without sacrificing durability.", img: IMG.macroCable },
  { title: "Made To Last", body: "Designed to retain comfort, shape and appearance through repeated use.", img: IMG.macroBrown },
  { title: "Thoughtful Construction", body: "Details designed around movement, fit and everyday performance.", img: "https://images.unsplash.com/photo-1595026525047-dfa997df8a4a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHxtYWNybyUyMGZhYnJpYyUyMHRleHR1cmUlMjBrbml0JTIwd29vbCUyMGNsb3NlJTIwdXB8ZW58MHx8fHwxNzg3MDQ3NjcwfDA&ixlib=rb-4.1.0&q=85" },
  { title: "Gift-Worthy Comfort", body: "Premium essentials that are practical, elegant and easy to gift.", img: "https://images.unsplash.com/photo-1632944967162-0dbdfc83ab62?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxrbml0dGVkJTIwd29vbCUyMHdpbnRlciUyMHNvY2tzJTIwZmxhdGxheSUyMG5ldXRyYWx8ZW58MHx8fHwxNzg3MDQ3NjcwfDA&ixlib=rb-4.1.0&q=85" },
];

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [collections, setCollections] = useState([]);
  const [hero, setHero] = useState(null);

  useEffect(() => {
    getCategories().then(setCategories).catch(() => {});
    getCollections().then(setCollections).catch(() => {});
    getHeroApi().then(setHero).catch(() => {});
  }, []);

  const h = hero || {
    eyebrow: "LEMCE", heading: "Comfort That Stays With You.",
    subheading: "Premium essentials engineered for exceptional softness, lasting comfort and effortless everyday living.",
    image: IMG.hero, primary_label: "Shop LEMCE", primary_to: "/collection/best-sellers",
    secondary_label: "Explore Collections", secondary_to: "/collection/new-at-lemce",
  };

  return (
    <div data-testid="home-page">
      <Seo title={h.heading} description={h.subheading} image={h.image} />
      {/* HERO */}
      <section className="relative h-[88vh] min-h-[600px] overflow-hidden grain-overlay">
        <Img src={h.image} alt={h.heading} priority base={1200} widths={[800, 1200, 1600]} sizes="100vw" className="absolute inset-0 w-full h-full object-cover animate-slow-zoom" />
        <div className="absolute inset-0 bg-lemce-matte/35" />
        <div className="relative h-full max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 flex flex-col justify-end pb-20 lg:pb-28">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}>
            {h.eyebrow && <p className="text-lemce-ivory/80 text-xs tracking-[0.35em] uppercase mb-5">{h.eyebrow}</p>}
            <h1 className="font-serif text-lemce-ivory text-4xl sm:text-6xl lg:text-7xl leading-[1.05] max-w-3xl">
              {h.heading}
            </h1>
            {h.subheading && <p className="text-lemce-ivory/85 text-base sm:text-lg mt-6 max-w-xl leading-relaxed">
              {h.subheading}
            </p>}
            <div className="flex flex-wrap gap-4 mt-9">
              {h.primary_label && <Link to={h.primary_to || "/"} data-testid="hero-shop-btn" className="bg-lemce-ivory text-lemce-matte px-9 py-4 text-xs tracking-widest uppercase hover:bg-lemce-beige transition-colors">
                {h.primary_label}
              </Link>}
              {h.secondary_label && <Link to={h.secondary_to || "/"} data-testid="hero-explore-btn" className="border border-lemce-ivory/70 text-lemce-ivory px-9 py-4 text-xs tracking-widest uppercase hover:bg-lemce-ivory hover:text-lemce-matte transition-colors">
                {h.secondary_label}
              </Link>}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CATEGORY NAV */}
      <section className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-20 lg:py-28">
        <ScrollReveal className="mb-12">
          <p className="text-[11px] tracking-widest uppercase text-lemce-grey mb-3">Explore</p>
          <h2 className="font-serif text-3xl sm:text-4xl">Shop by Category</h2>
        </ScrollReveal>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
          {["men", "women", "baby"].map((s) => categories.find((c) => c.slug === s)).filter(Boolean).map((c, i) => (
            <ScrollReveal key={c.slug} delay={i * 0.06}>
              <Link to={`/collection/${c.slug}`} data-testid={`category-${c.slug}`} className="group block relative aspect-[3/4] overflow-hidden bg-lemce-beige">
                <Img src={c.image} alt={c.title} base={700} widths={[400, 700]} sizes="(min-width:1024px) 25vw, 50vw" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-lemce-matte/70 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h3 className="font-serif text-lemce-ivory text-xl mb-1">{c.title}</h3>
                  <span className="inline-flex items-center gap-1.5 text-lemce-ivory/85 text-[11px] tracking-widest uppercase group-hover:gap-3 transition-all">
                    Shop Now <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </span>
                </div>
              </Link>
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* USP */}
      <section className="bg-lemce-cream/60 border-y border-lemce-beige/60 py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <ScrollReveal className="mb-12 max-w-xl">
            <p className="text-[11px] tracking-widest uppercase text-lemce-grey mb-3">The LEMCE Difference</p>
            <h2 className="font-serif text-3xl sm:text-5xl">Comfort, Refined.</h2>
          </ScrollReveal>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {USP.map((u, i) => (
              <ScrollReveal key={u.title} delay={i * 0.05} className={i === 0 ? "lg:row-span-2" : ""}>
                <div className={`group relative overflow-hidden bg-lemce-beige ${i === 0 ? "aspect-[3/4] lg:aspect-auto lg:w-full lg:h-full" : "aspect-[4/3]"}`}>
                  <Img src={u.img} alt={u.title} base={700} widths={[400, 700]} sizes="(min-width:1024px) 33vw, 50vw" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-lemce-matte/80 to-transparent" />
                  <div className="absolute bottom-0 p-6">
                    <h3 className="font-serif text-lemce-ivory text-xl mb-1.5">{u.title}</h3>
                    <p className="text-lemce-ivory/75 text-xs leading-relaxed max-w-xs">{u.body}</p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED COLLECTIONS */}
      {collections.map((col) => (
        <section key={col.slug} className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-16 lg:py-20" data-testid={`collection-section-${col.slug}`}>
          <ScrollReveal className="flex items-end justify-between mb-10">
            <div>
              <p className="text-[11px] tracking-widest uppercase text-lemce-grey mb-2">{col.subtitle}</p>
              <h2 className="font-serif text-3xl sm:text-4xl">{col.title}</h2>
            </div>
            <Link to={`/collection/${col.slug}`} className="hidden sm:inline-flex items-center gap-2 text-[11px] tracking-widest uppercase border-b border-lemce-matte pb-1 hover:gap-3 transition-all">
              View All <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
            </Link>
          </ScrollReveal>
          <div className="flex lg:grid lg:grid-cols-4 gap-5 lg:gap-6 overflow-x-auto no-scrollbar snap-x -mx-6 px-6 lg:mx-0 lg:px-0">
            {col.products.slice(0, 4).map((p, i) => (
              <div key={p.slug} className="min-w-[70%] sm:min-w-[45%] lg:min-w-0 snap-start">
                <ProductCard product={p} index={i} />
              </div>
            ))}
          </div>
        </section>
      ))}

      {/* DARK STORYTELLING */}
      <section className="bg-lemce-matte text-lemce-ivory grain-overlay">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2">
          <div className="relative aspect-[4/5] lg:aspect-auto lg:min-h-[640px] overflow-hidden">
            <Img src={IMG.lifestyle} alt="Editorial knitwear" base={900} widths={[600, 900, 1200]} sizes="(min-width:1024px) 50vw, 100vw" className="w-full h-full object-cover" />
          </div>
          <div className="flex flex-col justify-center px-8 sm:px-14 py-16 lg:py-24">
            <p className="text-[11px] tracking-widest uppercase text-lemce-taupe mb-5">Product Story</p>
            <h2 className="font-serif text-3xl sm:text-5xl leading-tight mb-6 text-lemce-ivory">
              Comfort shouldn't disappear after the first wash.
            </h2>
            <p className="text-white/70 leading-relaxed mb-6 max-w-md">
              Every LEMCE piece is engineered to keep its softness, shape and finish through repeated wear. We obsess over the fibre, the seam and the way it feels on day one hundred — not just day one.
            </p>
            <p className="font-serif text-xl text-lemce-taupe mb-9">Designed Around The Way You Move.</p>
            <Link to="/about" className="self-start border border-lemce-ivory/60 text-lemce-ivory px-8 py-3.5 text-xs tracking-widest uppercase hover:bg-lemce-ivory hover:text-lemce-matte transition-colors">
              The LEMCE Story
            </Link>
          </div>
        </div>
      </section>

      <TrustStrip />

      <Testimonials />

      {/* CLOSING */}
      <section className="relative h-[70vh] min-h-[480px] overflow-hidden grain-overlay">
        <Img src={IMG.closing} alt="LEMCE everyday comfort" base={1200} widths={[800, 1200, 1600]} sizes="100vw" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-lemce-matte/55" />
        <div className="relative h-full flex flex-col items-center justify-center text-center px-6">
          <ScrollReveal>
            <h2 className="font-serif text-lemce-ivory text-3xl sm:text-5xl lg:text-6xl leading-tight max-w-4xl mx-auto">
              The things you wear every day should feel extraordinary.
            </h2>
            <p className="text-lemce-ivory/80 mt-6 max-w-xl mx-auto leading-relaxed">
              Discover everyday essentials designed around softness, movement and lasting comfort.
            </p>
            <Link to="/collection/best-sellers" data-testid="closing-cta" className="inline-block mt-9 bg-lemce-ivory text-lemce-matte px-10 py-4 text-xs tracking-widest uppercase hover:bg-lemce-beige transition-colors">
              Discover LEMCE
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
